import { apiClient } from './axios';

import type {
  PaginatedServiceVehicles,
  ServiceDraft,
  ServiceHistoryApiToken,
  ServiceHistorySettings,
  ServicePdfAssetKind,
  ServicePdfTheme,
  ServiceHistoryModel,
  ServiceVehicleDetail,
  UpdateServiceHistorySettingsDto,
} from '@repo/shared-types';

/** Reading many PDFs with a Pro model can take a couple of minutes. */
const AI_TIMEOUT_MS = 5 * 60 * 1000;

const filesForm = (files: File[], model: ServiceHistoryModel) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  formData.append('model', model);
  return formData;
};

const multipart = { headers: { 'Content-Type': 'multipart/form-data' }, timeout: AI_TIMEOUT_MS };

export const serviceHistoryApi = {
  readFiles: async (files: File[], model: ServiceHistoryModel) => {
    const response = await apiClient.post<ServiceDraft>(
      '/service-history/read',
      filesForm(files, model),
      multipart,
    );
    return response.data;
  },

  saveDraft: async (draftId: string, registration: string) => {
    const response = await apiClient.post<ServiceVehicleDetail>(
      `/service-history/drafts/${draftId}/save`,
      { registration },
      { timeout: AI_TIMEOUT_MS },
    );
    return response.data;
  },

  listVehicles: async (params: { search?: string; page: number; limit: number }) => {
    const response = await apiClient.get<PaginatedServiceVehicles>('/service-history/vehicles', {
      params,
    });
    return response.data;
  },

  getVehicle: async (id: string) => {
    const response = await apiClient.get<ServiceVehicleDetail>(`/service-history/vehicles/${id}`);
    return response.data;
  },

  addDocuments: async (id: string, files: File[], model: ServiceHistoryModel) => {
    const response = await apiClient.post<ServiceVehicleDetail>(
      `/service-history/vehicles/${id}/documents`,
      filesForm(files, model),
      multipart,
    );
    return response.data;
  },

  removeDocument: async (id: string, documentId: string) => {
    const response = await apiClient.delete<ServiceVehicleDetail>(
      `/service-history/vehicles/${id}/documents/${documentId}`,
      { timeout: AI_TIMEOUT_MS },
    );
    return response.data;
  },

  regenerate: async (id: string, model: ServiceHistoryModel) => {
    const response = await apiClient.post<ServiceVehicleDetail>(
      `/service-history/vehicles/${id}/regenerate`,
      { model },
      { timeout: AI_TIMEOUT_MS },
    );
    return response.data;
  },

  deleteVehicle: async (id: string) => {
    await apiClient.delete(`/service-history/vehicles/${id}`, { timeout: 60000 });
  },

  editSummary: async (id: string, summary: string) => {
    const response = await apiClient.patch<ServiceVehicleDetail>(
      `/service-history/vehicles/${id}/summary`,
      { summary },
    );
    return response.data;
  },

  getSummaryPdfUrl: async (id: string) => {
    const response = await apiClient.get<{ url: string }>(
      `/service-history/vehicles/${id}/summary-pdf`,
      { timeout: 60000 },
    );
    return response.data.url;
  },

  getSettings: async () => {
    const response = await apiClient.get<ServiceHistorySettings>('/service-history/settings');
    return response.data;
  },

  updateSettings: async (dto: UpdateServiceHistorySettingsDto) => {
    const response = await apiClient.put<ServiceHistorySettings>('/service-history/settings', dto);
    return response.data;
  },

  /** The real PDF for an unsaved design, as a blob for the preview frame. */
  previewPdf: async (pdfTheme: ServicePdfTheme, vehicleId?: string) => {
    const response = await apiClient.post<Blob>(
      '/service-history/settings/preview-pdf',
      { pdf_theme: pdfTheme, ...(vehicleId ? { vehicle_id: vehicleId } : {}) },
      { responseType: 'blob', timeout: 60000 },
    );
    return response.data;
  },

  uploadThemeAsset: async (kind: ServicePdfAssetKind, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<{ path: string }>(
      `/service-history/settings/assets/${kind}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000 },
    );
    return response.data.path;
  },

  findByRegistration: async (registration: string) => {
    const response = await apiClient.get<{ vehicle: ServiceVehicleDetail | null }>(
      `/service-history/by-registration/${encodeURIComponent(registration)}`,
    );
    return response.data.vehicle;
  },

  getDocumentUrl: async (documentId: string) => {
    const response = await apiClient.get<{ url: string }>(
      `/service-history/documents/${documentId}/url`,
    );
    return response.data.url;
  },

  getTokens: async () => {
    const response = await apiClient.get<ServiceHistoryApiToken[]>('/service-history/tokens');
    return response.data;
  },

  createToken: async (name: string) => {
    const response = await apiClient.post<{ token: string; id: string }>(
      '/service-history/tokens',
      { name },
    );
    return response.data;
  },

  revokeToken: async (id: string) => {
    await apiClient.delete(`/service-history/tokens/${id}`);
  },
};
